import { Button, Card, Empty, Input, InputNumber, Select } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  EXAMPLE_KINDS,
  EXAMPLE_REGIMES,
  benefitOrderOptions,
  createExample,
  isEngineExample,
  nextExampleWeight,
} from "../reimbursementExamples";

const ReimbursementExamples = ({ values, setFieldValue }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [regime, setRegime] = useState(EXAMPLE_REGIMES.SOCIAL_SECURITY);
  const rows = get(values, "supportingInformation") || [];
  const coverages = get(values, "insurancePlanCoverages") || [];

  const visible = rows
    .map((row, index) => ({ row, index }))
    .filter(
      ({ row }) => isEngineExample(row) && get(row, "value.regime") === regime,
    )
    .sort(
      (left, right) =>
        (Number(left.row.weight) || 0) - (Number(right.row.weight) || 0),
    );

  const selectedOrders = visible.flatMap(({ row }) =>
    get(row, "value.benefitOrders", []),
  );
  const orderOptions = benefitOrderOptions(coverages, selectedOrders);

  const writeRows = (nextRows) =>
    setFieldValue("supportingInformation", nextRows);

  const patchExample = (index, valuePatch) => {
    writeRows(
      rows.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, value: { ...row.value, ...valuePatch } }
          : row,
      ),
    );
  };

  const addExample = () => {
    writeRows([
      ...rows,
      createExample({
        id: cuid(),
        regime,
        weight: nextExampleWeight(rows, regime),
      }),
    ]);
  };

  const removeExample = (index) => {
    const row = rows[index];
    if (row && row.persisted) {
      writeRows(
        rows.map((item, rowIndex) =>
          rowIndex === index
            ? { ...item, deletedDate: new Date().toISOString() }
            : item,
        ),
      );
      return;
    }
    writeRows(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const regimeOptions = [
    {
      value: EXAMPLE_REGIMES.SOCIAL_SECURITY,
      label: t("CONFIGURE.REIMBURSEMENT_EXAMPLES.REGIME_SOCIAL_SECURITY"),
    },
    {
      value: EXAMPLE_REGIMES.LOCAL,
      label: t("CONFIGURE.REIMBURSEMENT_EXAMPLES.REGIME_LOCAL"),
    },
  ];
  const kindOptions = [
    {
      value: EXAMPLE_KINDS.PERCENTAGE,
      label: t("CONFIGURE.REIMBURSEMENT_EXAMPLES.KIND_PERCENTAGE"),
    },
    {
      value: EXAMPLE_KINDS.FLAT,
      label: t("CONFIGURE.REIMBURSEMENT_EXAMPLES.KIND_FLAT"),
    },
  ];

  return (
    <div className="configure-step">
      <Card title={t("CONFIGURE.REIMBURSEMENT_EXAMPLES.TITLE")}>
        <p className="configure-step__muted m-bottom-16">
          {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.INTRO")}
        </p>
        <div className="m-bottom-16">
          <label className="input__label">
            {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.REGIME")}
          </label>
          <Select
            value={regime}
            options={regimeOptions}
            onChange={setRegime}
            className="configure-field--select"
          />
        </div>
        <div className="configure-stack">
          {visible.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("CONFIGURE.REIMBURSEMENT_EXAMPLES.EMPTY")}
            />
          )}
          {visible.map(({ row, index }) => {
            const value = row.value || {};
            return (
              <div className="reimbursement-example" key={row.id || index}>
                <div className="d-flex justify--between align--start gap-16">
                  <div className="flex--grow-1">
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.ACT")}
                    </label>
                    <Input
                      value={value.act || ""}
                      placeholder={t(
                        "CONFIGURE.REIMBURSEMENT_EXAMPLES.ACT_PLACEHOLDER",
                      )}
                      onChange={(event) =>
                        patchExample(index, { act: event.target.value })
                      }
                    />
                  </div>
                  <Button
                    type="text"
                    danger
                    icon={<BiTrash size={16} />}
                    onClick={() => removeExample(index)}
                  />
                </div>
                <div className="reimbursement-example__grid">
                  <div>
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.KIND")}
                    </label>
                    <Select
                      value={value.kind}
                      options={kindOptions}
                      onChange={(kind) => patchExample(index, { kind })}
                      className="reimbursement-example__control"
                    />
                  </div>
                  <div>
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.ORDER")}
                    </label>
                    <InputNumber
                      min={1}
                      value={row.weight}
                      className="reimbursement-example__control"
                      onChange={(weight) =>
                        writeRows(
                          rows.map((item, rowIndex) =>
                            rowIndex === index ? { ...item, weight } : item,
                          ),
                        )
                      }
                    />
                  </div>
                  <div className="reimbursement-example__wide">
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.BENEFITS")}
                    </label>
                    <Select
                      mode="multiple"
                      value={value.benefitOrders || []}
                      options={orderOptions}
                      placeholder={t(
                        "CONFIGURE.REIMBURSEMENT_EXAMPLES.BENEFITS_PLACEHOLDER",
                      )}
                      onChange={(benefitOrders) =>
                        patchExample(index, {
                          benefitOrders: (benefitOrders || []).map(Number),
                        })
                      }
                      className="reimbursement-example__control"
                    />
                  </div>
                  <div>
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.ACTUAL_COST")}
                    </label>
                    <InputNumber
                      min={0}
                      value={value.actualCost}
                      className="reimbursement-example__control"
                      onChange={(actualCost) =>
                        patchExample(index, { actualCost })
                      }
                    />
                  </div>
                  <div>
                    <label className="input__label">
                      {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.REIMBURSEMENT_BASE")}
                    </label>
                    <InputNumber
                      min={0}
                      value={value.reimbursementBase}
                      className="reimbursement-example__control"
                      onChange={(reimbursementBase) =>
                        patchExample(index, { reimbursementBase })
                      }
                    />
                  </div>
                  <div>
                    <label className="input__label">
                      {t(
                        "CONFIGURE.REIMBURSEMENT_EXAMPLES.SOCIAL_SECURITY_RATE",
                      )}
                    </label>
                    <InputNumber
                      min={0}
                      max={1}
                      step={0.01}
                      value={value.socialSecurityRate}
                      className="reimbursement-example__control"
                      onChange={(socialSecurityRate) =>
                        patchExample(index, { socialSecurityRate })
                      }
                    />
                    <p className="configure-step__hint">
                      {t(
                        "CONFIGURE.REIMBURSEMENT_EXAMPLES.SOCIAL_SECURITY_RATE_HINT",
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="input__label">
                      {t(
                        "CONFIGURE.REIMBURSEMENT_EXAMPLES.SOCIAL_SECURITY_REFUND",
                      )}
                    </label>
                    <InputNumber
                      min={0}
                      value={value.socialSecurityRefund}
                      className="reimbursement-example__control"
                      onChange={(socialSecurityRefund) =>
                        patchExample(index, { socialSecurityRefund })
                      }
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!orderOptions.length && (
          <p className="configure-step__hint m-top-16">
            {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.BENEFITS_EMPTY")}
          </p>
        )}
        <Button
          type="primary"
          className="m-top-16"
          icon={<BiPlus size={16} />}
          onClick={addExample}
        >
          {t("CONFIGURE.REIMBURSEMENT_EXAMPLES.ADD")}
        </Button>
      </Card>
    </div>
  );
};

export default ReimbursementExamples;
